import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { Button } from "./Button";
import { I } from "./icons";

export interface ActionBarPrimary {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  /** why it is disabled, in words — shown above the button with a lock, and pointed at by it */
  reason?: string;
  busy?: boolean;
  /** "danger" for a destructive confirm (delete, remove): the red button, same place */
  tone?: "danger";
}

export interface ActionBarSecondary {
  label: string;
  onClick: () => void;
}

/**
 * The docked primary (SNIPPETS §12): a ghost secondary on the left, the primary growing right,
 * both 48 tall. Disabled with a reason: the reason sits above the button, which points at it
 * with `aria-describedby`.
 *
 * When to use: a focused page or a sheet with one main action. Render it last in the page's
 * content. It docks to the bottom of the page — above the LiveBar and tab bar when they show, at
 * the screen's edge (safe area included) when they do not — and leaves a spacer of its own height
 * in the flow so nothing scrolls under it. In a `Sheet`, pass it as the sheet's `footer` with
 * `inSheet`: no rule, no docking, 8px above.
 */
export function ActionBar({
  primary,
  secondary,
  also,
  inSheet,
  lead,
}: {
  primary: ActionBarPrimary;
  secondary?: ActionBarSecondary;
  /** a second ghost between the two — Back · Skip · Next on an optional question (kit §28) */
  also?: ActionBarSecondary;
  inSheet?: boolean;
  /** one sentence above the buttons, with an info mark: why the secondary is offered — the
   *  cycle line on the session screen (ADR-057). A disabled primary's reason takes its place. */
  lead?: ReactNode;
}) {
  const whyId = useId();
  const bar = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(72);

  useLayoutEffect(() => {
    const el = bar.current;
    if (!el || inSheet) return;
    const set = () => setH(el.offsetHeight);
    set();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [inSheet]);

  const why = primary.disabled && primary.reason ? primary.reason : null;
  const led = !why && lead !== undefined && lead !== null;
  const cls = `k-actionbar${inSheet ? " k-actionbar--sheet" : ""}${why ? " k-actionbar--why" : ""}${
    led ? " k-actionbar--lead" : ""
  }`;

  const buttons = (
    <>
      {secondary && !why ? (
        <Button variant="ghost" size="lg" onClick={secondary.onClick}>
          {secondary.label}
        </Button>
      ) : null}
      {also && !why ? (
        <Button variant="ghost" size="lg" onClick={also.onClick}>
          {also.label}
        </Button>
      ) : null}
      <Button
        variant={primary.tone === "danger" ? "danger" : "primary"}
        size="lg"
        grow={!why}
        icon={primary.icon}
        onClick={primary.onClick}
        disabled={primary.disabled}
        busy={primary.busy}
        describedBy={why ? whyId : undefined}
      >
        {primary.label}
      </Button>
    </>
  );

  return (
    <>
      {inSheet ? null : <div className="k-actionbar-spacer" style={{ height: h }} aria-hidden="true" />}
      <div ref={bar} className={cls}>
        {why ? (
          <p id={whyId} className="k-actionbar__why">
            {I.lock}
            {why}
          </p>
        ) : null}
        {led ? (
          <>
            <p className="k-actionbar__lead">
              {I.info}
              <span>{lead}</span>
            </p>
            <div className="k-actionbar__row">{buttons}</div>
          </>
        ) : (
          buttons
        )}
      </div>
    </>
  );
}
